import React, { useState, useEffect, useRef } from "react";
import {
  Bot,
  Signal,
  Wifi,
  Battery,
  ChevronLeft,
  Video,
  Phone,
  Smile,
  Mic,
  Play,
  BarChart3,
} from "lucide-react";

const WAVE_BARS = [6, 10, 4, 8, 12, 7, 9, 5, 11, 8, 6, 10, 4, 9, 7, 11, 5, 8];

function MensagemTexto({ autor, conteudo }) {
  const isDono = autor === "dono";
  return (
    <div className={`flex ${isDono ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[80%] px-3 py-2 rounded-2xl text-[13px] leading-snug ${
          isDono
            ? "bg-green-500 text-black rounded-br-sm font-medium"
            : "bg-[#1F1F1F] text-zinc-200 rounded-bl-sm border border-white/5"
        }`}
      >
        {conteudo}
      </div>
    </div>
  );
}

function MensagemAudio({ autor, duracao }) {
  const isDono = autor === "dono";
  return (
    <div className={`flex ${isDono ? "justify-end" : "justify-start"}`}>
      <div
        className={`flex items-center gap-2 px-3 py-2 rounded-2xl min-w-[180px] ${
          isDono
            ? "bg-green-500 text-black rounded-br-sm"
            : "bg-[#1F1F1F] text-zinc-200 rounded-bl-sm border border-white/5"
        }`}
      >
        <div
          className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
            isDono ? "bg-black/20" : "bg-green-500/20"
          }`}
        >
          <Play
            className={`w-3 h-3 ${isDono ? "text-black" : "text-green-400"}`}
            fill="currentColor"
          />
        </div>
        <div className="flex items-center gap-[2px] flex-1">
          {WAVE_BARS.map((h, i) => (
            <div
              key={i}
              className={`w-[2px] rounded-full ${
                isDono ? "bg-black/40" : "bg-zinc-500"
              }`}
              style={{ height: `${h}px` }}
            />
          ))}
        </div>
        <span
          className={`text-[10px] font-medium flex-shrink-0 ${
            isDono ? "text-black/70" : "text-zinc-500"
          }`}
        >
          {duracao}
        </span>
      </div>
    </div>
  );
}

function MensagemGrafico({ titulo, labels, valores, destaque }) {
  const max = Math.max(...valores);
  return (
    <div className="flex justify-start">
      <div className="w-[85%] bg-[#1F1F1F] border border-white/5 rounded-2xl rounded-bl-sm p-3 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <BarChart3 className="w-3 h-3 text-green-400 flex-shrink-0" />
            <span className="text-[11px] font-semibold text-white truncate">
              {titulo}
            </span>
          </div>
          <span className="text-[10px] text-green-400 font-bold flex-shrink-0">
            {destaque}
          </span>
        </div>
        <div className="flex items-end justify-between gap-1.5 h-20 pt-2">
          {valores.map((valor, i) => {
            const altura = (valor / max) * 100;
            return (
              <div
                key={i}
                className="flex-1 flex flex-col items-center gap-1 min-w-0"
              >
                <div
                  className="w-full bg-gradient-to-t from-green-600 to-green-400 rounded-t-md shadow-lg shadow-green-500/30"
                  style={{ height: `${altura}%`, minHeight: "8px" }}
                />
                <span className="text-[8px] text-zinc-500 font-medium truncate w-full text-center">
                  {labels[i]}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Mensagem({ mensagem }) {
  if (mensagem.tipo === "audio") {
    return <MensagemAudio autor={mensagem.autor} duracao={mensagem.duracao} />;
  }
  if (mensagem.tipo === "grafico") {
    return (
      <MensagemGrafico
        titulo={mensagem.titulo}
        labels={mensagem.labels}
        valores={mensagem.valores}
        destaque={mensagem.destaque}
      />
    );
  }
  return <MensagemTexto autor={mensagem.autor} conteudo={mensagem.conteudo} />;
}

export default function MockupCelular({ conversa }) {
  const [visibleCount, setVisibleCount] = useState(0);
  const [showTyping, setShowTyping] = useState(false);
  const [epoch, setEpoch] = useState(0);
  const [disparou, setDisparou] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setDisparou(true);
      },
      { threshold: 0.25 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!disparou || !conversa || conversa.length === 0) return;

    const reducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reducedMotion) {
      setVisibleCount(conversa.length);
      setShowTyping(false);
      return;
    }

    setVisibleCount(0);
    setShowTyping(false);

    const timeouts = [];
    let cumulative = 500;

    conversa.forEach((msg, i) => {
      const prev = conversa[i - 1];
      const needsTyping =
        msg.autor === "ivo" && (!prev || prev.autor === "dono");

      if (needsTyping) {
        timeouts.push(setTimeout(() => setShowTyping(true), cumulative));
        cumulative += 1500;
        timeouts.push(
          setTimeout(() => {
            setShowTyping(false);
            setVisibleCount(i + 1);
          }, cumulative)
        );
      } else {
        timeouts.push(setTimeout(() => setVisibleCount(i + 1), cumulative));
      }

      cumulative += 800;
    });

    cumulative += 3000;
    timeouts.push(
      setTimeout(() => {
        setEpoch((e) => e + 1);
      }, cumulative)
    );

    return () => timeouts.forEach(clearTimeout);
  }, [disparou, epoch, conversa]);

  return (
    <div className="relative mx-auto w-full max-w-[340px]">
      {/* Glow externo */}
      <div className="absolute -inset-6 bg-gradient-to-br from-green-500/20 via-blue-500/10 to-green-500/20 rounded-[3rem] blur-3xl opacity-60 pointer-events-none" />

      {/* Frame externo */}
      <div className="relative bg-black rounded-[2.5rem] p-3 shadow-2xl shadow-green-500/20 border border-white/10">
        {/* Bezel interno */}
        <div className="bg-[#0A0A0A] rounded-[2rem] overflow-hidden border border-white/5">
          {/* Status bar */}
          <div className="flex items-center justify-between px-6 pt-3 pb-2 bg-[#0A0A0A]">
            <span className="text-white text-xs font-semibold">14:32</span>
            <div className="flex items-center gap-1">
              <Signal className="w-3 h-3 text-white" />
              <Wifi className="w-3 h-3 text-white" />
              <Battery className="w-4 h-4 text-white" />
            </div>
          </div>

          {/* Notch / Dynamic Island */}
          <div className="flex justify-center -mt-1 mb-2">
            <div className="w-24 h-6 bg-black rounded-full" />
          </div>

          {/* Header conversa WhatsApp */}
          <div className="flex items-center gap-3 px-4 py-3 bg-[#1A1A1A] border-b border-white/5">
            <ChevronLeft className="w-5 h-5 text-zinc-400 flex-shrink-0" />
            <div className="relative flex-shrink-0">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center">
                <Bot className="w-5 h-5 text-black" />
              </div>
              <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-[#1A1A1A]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white font-semibold text-sm truncate">
                Ivo · Evolzap
              </div>
              <div className="text-zinc-500 text-[10px] flex items-center gap-1">
                <span className="w-1 h-1 bg-green-500 rounded-full" />
                online agora
              </div>
            </div>
            <Video className="w-5 h-5 text-zinc-400 flex-shrink-0" />
            <Phone className="w-4 h-4 text-zinc-400 flex-shrink-0" />
          </div>

          {/* Corpo da conversa */}
          <div
            ref={containerRef}
            className="bg-[#0A0A0A] p-3 space-y-2 h-[480px] overflow-hidden"
            data-testid="ivo-conversa"
          >
            {conversa.slice(0, visibleCount).map((msg, i) => (
              <div key={`${epoch}-${i}`} className="mk-chat-in">
                <Mensagem mensagem={msg} />
              </div>
            ))}
            {showTyping && (
              <div className="flex justify-start mk-chat-in" key={`typing-${epoch}`}>
                <div className="bg-[#1F1F1F] border border-white/5 rounded-2xl rounded-bl-sm px-3 py-2 flex items-center gap-1">
                  <span className="mk-chat-dot"></span>
                  <span className="mk-chat-dot"></span>
                  <span className="mk-chat-dot"></span>
                </div>
              </div>
            )}
          </div>

          {/* Footer input */}
          <div className="flex items-center gap-2 px-3 py-2.5 bg-[#1A1A1A] border-t border-white/5">
            <Smile className="w-5 h-5 text-zinc-500 flex-shrink-0" />
            <div className="flex-1 bg-[#0F0F0F] border border-white/5 rounded-full px-3 py-1.5 text-zinc-600 text-xs">
              Pergunte ou mande áudio...
            </div>
            <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
              <Mic className="w-4 h-4 text-black" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
